import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import CGUAcceptanceModal from '../components/CGUAcceptanceModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AuthCallback() {
  const navigate = useNavigate();
  const { storeToken, setAuthenticatedUser } = useAuth();
  const hasProcessed = useRef(false);
  const [showCGUModal, setShowCGUModal] = useState(false);
  const [pendingUser, setPendingUser] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      console.log('[AuthCallback] Processing OAuth callback...');
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (!sessionIdMatch) {
        console.log('[AuthCallback] No session_id in hash, redirecting home');
        navigate('/');
        return;
      }

      const sessionId = sessionIdMatch[1];
      console.log('[AuthCallback] Session ID found:', sessionId.substring(0, 20) + '...');

      try {
        const response = await fetch(`${API_URL}/api/auth/session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ session_id: sessionId })
        });

        console.log('[AuthCallback] Session exchange response:', response.status);

        if (response.ok) {
          const userData = await response.json();
          console.log('[AuthCallback] User data received:', userData.email);
          
          // IMPORTANT: Store the session token for cross-domain auth
          if (userData.session_token) {
            console.log('[AuthCallback] Storing session token');
            storeToken(userData.session_token);
          }
          
          // Check if user has accepted terms
          if (!userData.terms_accepted) {
            console.log('[AuthCallback] Terms not accepted, showing CGU modal');
            setPendingUser({
              email: String(userData.email || ''),
              name: String(userData.name || ''),
              user_id: String(userData.user_id || ''),
              session_token: userData.session_token
            });
            setShowCGUModal(true);
          } else {
            console.log(`[AuthCallback ${Date.now()}] ✓ OAuth successful, setting auth state`);
            // Set authenticated state directly
            setAuthenticatedUser(userData, userData.session_token);
            // Use React Router navigate instead of full page reload
            console.log(`[AuthCallback ${Date.now()}] Navigating to /dashboard via React Router`);
            navigate('/dashboard', { replace: true });
          }
        } else {
          console.error('[AuthCallback] Session exchange failed:', response.status);
          navigate('/');
        }
      } catch (error) {
        console.error('[AuthCallback] Error:', error);
        navigate('/');
      }
    };

    processAuth();
  }, [navigate, storeToken, setAuthenticatedUser]);

  const handleAcceptCGU = async () => {
    console.log(`[AuthCallback ${Date.now()}] CGU accepted, navigating to dashboard`);
    // Token is already stored from processAuth
    setShowCGUModal(false);
    navigate('/dashboard', { replace: true });
  };

  const handleDeclineCGU = async () => {
    console.log('[AuthCallback] CGU declined, logging out');
    // Logout the user if they decline
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('[AuthCallback] Logout error:', error);
    }
    setShowCGUModal(false);
    navigate('/');
  };

  if (showCGUModal) {
    return (
      <div className="min-h-screen bg-black-rich">
        <CGUAcceptanceModal
          isOpen={showCGUModal}
          onAccept={handleAcceptCGU}
          onDecline={handleDeclineCGU}
          userEmail={pendingUser?.email}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black-rich flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-gold-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-zinc-400">Connexion en cours...</p>
      </div>
    </div>
  );
}
